// 📄 useConfirmacion.ts
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros.
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveConfirmationConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, ConfirmationFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const CONFIRM_STEP_IDS = ['confirm_params', 'confirm_exit', 'confirm_control', 'confirm_job'];

const STEP_KEY_MAP: Record<string, string> = {
  confirm_params: 'step_params', confirm_exit: 'step_exit', confirm_control: 'step_control', confirm_job: 'step_job'
};

const DEFAULT_FORM: ConfirmationFormData = {
  ocEnabledParam: false,
  exitMM06E001Active: false, include01Added: false, include02Added: false,
  confirmationControlKey: 'Z004', ackSequence: 'AB Order Acknowledgment', deliverySequence: 'LA Inbound Delivery',
  jobName: 'ARBCIG_INBOUND_IDOC_SUR', jobVariant: '', jobStatus: 'Pendiente',
  requiresOT: false, otNumber: '', comments: '',
};

export function useConfirmacion() {
  const { projectId } = useProject();
  const [activeStep, setActiveStep] = useState('confirm_params');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<ConfirmationFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: keyof ConfirmationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    if (devData.checklistProgress) {
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && CONFIRM_STEP_IDS.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<ConfirmationFormData> = {};
    const confirm = devData.channels?.orderConfirmation;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.orderConfirmation : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (confirm) {
      if (confirm.step_params) {
        if (activeStep === 'confirm_params') {
          const requiresOT = confirm.step_params.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? confirm.step_params : (envChannelData?.step_params || {});
          initialFormData.ocEnabledParam = source.ocEnabledParam || false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, confirm_params: confirm.step_params.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, confirm_params: confirm.step_params.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, confirm_params: confirm.step_params.customDevelopment || emptyCustomDevelopment() }));
        qasMap.confirm_params = confirm.step_params.transport?.QAS || emptyTransportConfirmation();
        prdMap.confirm_params = confirm.step_params.transport?.PRD || emptyTransportConfirmation();
      }
      if (confirm.step_exit) {
        if (activeStep === 'confirm_exit') {
          const requiresOT = confirm.step_exit.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? confirm.step_exit : (envChannelData?.step_exit || {});
          initialFormData.exitMM06E001Active = source.exitMM06E001Active || false;
          initialFormData.include01Added = source.include01Added || false;
          initialFormData.include02Added = source.include02Added || false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, confirm_exit: confirm.step_exit.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, confirm_exit: confirm.step_exit.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, confirm_exit: confirm.step_exit.customDevelopment || emptyCustomDevelopment() }));
        qasMap.confirm_exit = confirm.step_exit.transport?.QAS || emptyTransportConfirmation();
        prdMap.confirm_exit = confirm.step_exit.transport?.PRD || emptyTransportConfirmation();
      }
      if (confirm.step_control) {
        if (activeStep === 'confirm_control') {
          const requiresOT = confirm.step_control.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? confirm.step_control : (envChannelData?.step_control || {});
          initialFormData.confirmationControlKey = source.confirmationControlKey || 'Z004';
          initialFormData.ackSequence = source.ackSequence || 'AB Order Acknowledgment';
          initialFormData.deliverySequence = source.deliverySequence || 'LA Inbound Delivery';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, confirm_control: confirm.step_control.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, confirm_control: confirm.step_control.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, confirm_control: confirm.step_control.customDevelopment || emptyCustomDevelopment() }));
        qasMap.confirm_control = confirm.step_control.transport?.QAS || emptyTransportConfirmation();
        prdMap.confirm_control = confirm.step_control.transport?.PRD || emptyTransportConfirmation();
      }
      if (confirm.step_job) {
        if (activeStep === 'confirm_job') {
          const requiresOT = confirm.step_job.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? confirm.step_job : (envChannelData?.step_job || {});
          initialFormData.jobName = source.jobName || 'ARBCIG_INBOUND_IDOC_SUR';
          initialFormData.jobVariant = source.jobVariant || '';
          initialFormData.jobStatus = source.jobStatus || 'Pendiente';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, confirm_job: confirm.step_job.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, confirm_job: confirm.step_job.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, confirm_job: confirm.step_job.customDevelopment || emptyCustomDevelopment() }));
        qasMap.confirm_job = confirm.step_job.transport?.QAS || emptyTransportConfirmation();
        prdMap.confirm_job = confirm.step_job.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.orderConfirmation;
    const prdChannelData = configSource?.environments?.PRD?.channels?.orderConfirmation;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    CONFIRM_STEP_IDS.forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración de Confirmación:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    if (activeStep === 'confirm_control' && !formData.confirmationControlKey.trim()) {
      setValidationError("La Control Key es obligatoria."); return;
    }
    if (activeStep === 'confirm_job' && !formData.jobName.trim()) {
      setValidationError("El nombre del programa (Job) es obligatorio."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveConfirmationConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'orderConfirmation', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'orderConfirmation', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep, setActiveStep, completedSteps, formData, handleFieldChange,
    requiresOTMap, setRequiresOTMap, otNumberMap, setOtNumberMap,
    customDevMap, setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError, setValidationError, saveStatus, loadingData,
    handleSave, selectedEnv, handleEnvChange,
  };
}
